import React, { useEffect, useMemo, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { fetchPathPracticeInit, updatePathPracticeProgress } from '../../api/fetchPath';
import { playUserAudio } from '../../utils/dialogUtils';
import { useAuth } from '../../context/AuthContext';
import { useAppConfig } from '../../context/AppConfigContext';
import styles from './PathPractice.module.css';

const buildHint = (translation, mode) => {
  if (!translation) return '';
  if (mode === 'easy') {
    return translation
      .split(' ')
      .map((word) => (word.length >= 3 ? `${word[0]}${'_'.repeat(word.length - 1)}` : '_'.repeat(word.length)))
      .join(' ');
  }
  return translation
    .split(' ')
    .map((word) => '_'.repeat(word.length))
    .join(' ');
};

const PathPractice = ({ userId, step, onBack }) => {
  const { isAndroidApp } = useAuth();
  const { site } = useAppConfig();
  const isNative = Capacitor.isNativePlatform();
  const combinedIsAndroidApp = isAndroidApp || isNative;
  const mode = step?.step_type === 'medium' ? 'medium' : 'easy';

  const [phrases, setPhrases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showTranslation, setShowTranslation] = useState(false);
  const [saving, setSaving] = useState(false);

  const currentPhrase = useMemo(() => phrases[currentIndex], [phrases, currentIndex]);

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        setLoading(true);
        const result = await fetchPathPracticeInit({
          userId,
          stepId: step?.id,
          chunkIds: step?.chunk_ids,
          totalItems: step?.total_items
        });
        if (active) {
          setPhrases(result?.phrases || []);
          setCurrentIndex(0);
          setShowTranslation(false);
        }
      } catch (_) {
        if (active) setPhrases([]);
      } finally {
        if (active) setLoading(false);
      }
    };
    if (step?.id) load();
    return () => {
      active = false;
    };
  }, [userId, step?.id]);

  useEffect(() => {
    if (!currentPhrase?.audio) return;
    const src = /^https?:\/\//i.test(currentPhrase.audio)
      ? currentPhrase.audio
      : `${combinedIsAndroidApp ? site : ''}${currentPhrase.audio}`;
    playUserAudio(src);
  }, [currentPhrase?.audio, combinedIsAndroidApp]);

  const moveNext = () => {
    setShowTranslation(false);
    if (currentIndex + 1 < phrases.length) {
      setCurrentIndex((prev) => prev + 1);
    } else {
      onBack?.();
    }
  };

  const handleKnown = async () => {
    if (!step?.id) {
      moveNext();
      return;
    }
    try {
      setSaving(true);
      await updatePathPracticeProgress({ userId, stepId: step.id, delta: 1 });
      moveNext();
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className={styles.loading}>Загрузка...</div>;
  }

  if (!currentPhrase) {
    return (
      <div className={styles.empty}>
        <div className={styles.emptyTitle}>Фразы закончились</div>
        <button className={styles.primaryButton} onClick={onBack}>
          Назад
        </button>
      </div>
    );
  }

  const audioSrc = currentPhrase.audio
    ? (/^https?:\/\//i.test(currentPhrase.audio)
      ? currentPhrase.audio
      : `${combinedIsAndroidApp ? site : ''}${currentPhrase.audio}`)
    : null;

  return (
    <div className={styles.container}>
      <div className={styles.topBar}>
        <button className={styles.backButton} onClick={onBack}>Назад</button>
        <div className={styles.progress}>{currentIndex + 1}/{phrases.length}</div>
      </div>

      <div className={styles.card}>
        <div className={styles.text}>{currentPhrase.text}</div>
        <div className={styles.hint}>{buildHint(currentPhrase.translation, mode)}</div>
        {audioSrc && (
          <button className={styles.audioButton} onClick={() => playUserAudio(audioSrc)}>
            🔊
          </button>
        )}
      </div>

      <div className={styles.actions}>
        <button className={styles.secondaryButton} onClick={() => setShowTranslation((v) => !v)}>
          {showTranslation ? 'Скрыть перевод' : 'Показать перевод'}
        </button>
      </div>

      {showTranslation && (
        <div className={styles.translationCard}>
          {currentPhrase.translation}
        </div>
      )}

      <div className={styles.actions}>
        <button className={styles.secondaryButton} onClick={moveNext}>Не знаю</button>
        <button className={styles.primaryButton} onClick={handleKnown} disabled={saving}>Знаю</button>
      </div>
    </div>
  );
};

export default PathPractice;
